/**
 * SidebarIdentityBlock — Logo strip + user identity + credits + quick stats
 * 2026-09-27 (cc_tasks row 80): "Set up credits" opens /pricing, not the
 * nonexistent /#pricing anchor.
 */
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useArsenalModal } from "@/components/ArsenalModal";
import { Logo } from "@/components/Logo";
import { useSidebar } from "@/components/ui/sidebar";
import type { Tables } from "@/integrations/supabase/types";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRecordMode } from "@/hooks/useRecordMode";

type Profile = Tables<"profiles">;

interface SidebarIdentityBlockProps {
  profile: Profile | null;
}

function useActiveSequenceCount(userId: string | undefined) {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      try {
        const { count: c } = await (supabase
          .from("sequences") as any)
          .select("id", { count: "exact", head: true })
          .eq("is_system", true);
        setCount(c ?? 0);
      } catch {
        setCount(null);
      }
    };
    load();
  }, [userId]);
  return count;
}

function getRoleBadgeStyle(role: string | null | undefined) {
  const r = (role || "").toLowerCase();
  if (r.includes("superadmin") || r.includes("admin"))
    return { background: "rgba(30,58,95,0.8)", color: "#60a5fa" };
  if (r === "agent")
    return { background: "rgba(0,201,167,0.15)", color: "#00C9A7" };
  if (r === "manager")
    return { background: "rgba(124,58,237,0.15)", color: "#a78bfa" };
  if (r === "executive" || r.includes("c-suite") || r.includes("vp") || r.includes("exec"))
    return { background: "rgba(217,119,6,0.15)", color: "#fbbf24" };
  return { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" };
}

function getRoleLabel(role: string | null | undefined) {
  if (!role) return "MEMBER";
  const r = role.toLowerCase();
  if (r.includes("superadmin") || r.includes("admin")) return "ADMIN";
  if (r === "executive" || r.includes("c-suite") || r.includes("vp") || r.includes("exec")) return "EXECUTIVE";
  if (r === "manager") return "MANAGER";
  if (r === "agent") return "AGENT";
  return "MEMBER";
}

function getCreditColorState(remaining: number, limit: number) {
  if (limit <= 0) return "neutral";
  const ratio = remaining / limit;
  if (remaining === 0) return "critical";
  if (ratio < 0.2) return "critical";
  if (ratio < 0.5) return "warning";
  return "healthy";
}

const CREDIT_STYLES = {
  healthy: { border: "rgba(0,201,167,0.4)", color: "#00C9A7", bg: "rgba(0,201,167,0.08)", fill: "#00C9A7" },
  warning: { border: "rgba(251,191,36,0.4)", color: "#fbbf24", bg: "rgba(251,191,36,0.08)", fill: "#fbbf24" },
  critical: { border: "#ef4444", color: "white", bg: "#ef4444", fill: "#ef4444" },
  neutral: { border: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)", bg: "transparent", fill: "rgba(255,255,255,0.2)" },
};

export function SidebarIdentityBlock({ profile }: SidebarIdentityBlockProps) {
  const navigate = useNavigate();
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { openArsenal } = useArsenalModal();
  const activeSeqCount = useActiveSequenceCount(profile?.id);
  const recordMode = useRecordMode();

  if (collapsed) return null;

  const userInitials = profile?.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : profile?.email?.slice(0, 2).toUpperCase() || "U";

  const role = profile?.role_in_company;
  const roleLabel = getRoleLabel(role);
  const isExecutive = roleLabel === "EXECUTIVE";
  const isAdmin = roleLabel === "ADMIN";
  const roleBadge = getRoleBadgeStyle(role);

  const creditsRemaining = (profile as any)?.credits_remaining ?? null;
  const creditsLimit = (profile as any)?.credits_limit ?? null;
  const arsenalSlots = (profile as any)?.arsenal_slots_loaded ?? 0;
  const creditState = creditsLimit ? getCreditColorState(creditsRemaining ?? 0, creditsLimit) : "neutral";
  const cs = CREDIT_STYLES[creditState];

  return (
    <div>
      {/* ── Logo Strip ── */}
      <div
        className="flex items-center justify-between"
        style={{ height: 44, padding: "0 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        <a href="/dashboard" aria-label="MentionFox dashboard">
          <Logo variant="full" size="md" />
        </a>
        {import.meta.env.DEV && !recordMode && (
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.15)" }}>v1.0</span>
        )}
      </div>

      {/* ── Identity Block ── */}
      <div style={{ padding: "14px 16px 12px", background: "rgba(255,255,255,0.02)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        {/* Row 1: Avatar + Name + Company + Role */}
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate("/settings/profile")}>
            <Avatar className="h-9 w-9">
              {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
              <AvatarFallback style={{ background: "#00C9A7", color: "white", fontWeight: 600, fontSize: 12 }}>{userInitials}</AvatarFallback>
            </Avatar>
          </div>
          <div className="min-w-0 flex-1">
            <p style={{ fontSize: 13, fontWeight: 500, color: "white", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {profile?.full_name || profile?.email || "User"}
            </p>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {profile?.company_name || "—"}
            </p>
            {!recordMode && (
              <span
                className="inline-flex items-center"
                style={{
                  padding: "2px 6px",
                  borderRadius: 8,
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  marginTop: 2,
                  lineHeight: 1.2,
                  ...roleBadge,
                }}
              >
                {roleLabel}
              </span>
            )}
          </div>
        </div>

        {/* Row 2: Credits (hidden for executives and admins) */}
        {!isExecutive && !isAdmin && (
          <div style={{ marginTop: 10 }}>
            <div className="flex items-center justify-between">
              {creditsRemaining != null ? (
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
                  <span style={{ color: "#00C9A7" }}>⚡</span> {creditsRemaining} credits
                </span>
              ) : (
                <button
                  onClick={() => navigate("/pricing")}
                  title="See plans and credit packs"
                  style={{ fontSize: 12, color: "#00C9A7", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  ⚡ Set up credits →
                </button>
              )}
              {creditsRemaining != null && (
                <button
                  onClick={() => {/* Add Credits modal placeholder */}}
                  className={creditState === "critical" ? "animate-pulse" : ""}
                  style={{
                    fontSize: 11,
                    padding: "2px 10px",
                    borderRadius: 10,
                    border: `1px solid ${cs.border}`,
                    color: cs.color,
                    background: cs.bg,
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  {creditsRemaining === 0 ? "⚠ Add Credits" : "+ Add Credits"}
                </button>
              )}
            </div>
            {creditsLimit != null && creditsLimit > 0 && (
              <div style={{ marginTop: 5, height: 3, borderRadius: 2, width: "100%", background: "rgba(255,255,255,0.06)" }}>
                <div
                  style={{
                    width: `${Math.min(100, ((creditsRemaining ?? 0) / creditsLimit) * 100)}%`,
                    height: "100%",
                    borderRadius: 2,
                    background: cs.fill,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Admin label row */}
        {isAdmin && (
          <div style={{ marginTop: 10 }}>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>Admin account</span>
          </div>
        )}

        {/* Row 3: Quick stats (hidden for executives and admins) */}
        {!isExecutive && !isAdmin && (
          <div className="flex items-center gap-3" style={{ marginTop: 8 }}>
            <button
              onClick={openArsenal}
              style={{ fontSize: 11, color: arsenalSlots >= 9 ? "#00C9A7" : "rgba(255,255,255,0.4)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              {arsenalSlots >= 9 ? "Arsenal ✓" : `Arsenal ${arsenalSlots}/9`}
            </button>
            {activeSeqCount !== null && (
              <button
                onClick={() => navigate("/dashboard/sequences")}
                style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              >
                {activeSeqCount > 0 ? `${activeSeqCount} active` : "No sequences"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
